import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { MERCH_STORE_URL, API } from "@/assets";
import { Mail, MapPin, FileText, ArrowUpRight, Megaphone, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);
const PRESS_EMAIL = "media.press@cold-blooded-records.com";

const FALLBACK_ARTICLES = [
  {
    id: "la-film-school-enrollment",
    date: "September 2026",
    category: "Executive News & Audio Engineering",
    title: "Heather Da Boa Enrolls in The Los Angeles Film School B.S. in Music Production",
    body: "Label founder and lead producer Heather Da Boa Parrish has officially enrolled in the Bachelor of Science in Music Production degree program at The Los Angeles Film School / Los Angeles Recording School. This academic milestone brings advanced commercial mixing, mastering, and sound design techniques directly into the DGE: Cold-Blooded Records LLC production suite.",
    link_label: null, link_url: null,
  },
  {
    id: "soundscapes-beat-catalog-launch",
    date: "September 2026",
    category: "Audio Production & Beat Licensing",
    title: "Official Launch: Cold-Blooded Soundscapes™ Beat Catalog",
    body: "DGE: Cold-Blooded Records LLC has officially launched its automated beat licensing portal, featuring the signature sound lane Cold-Blooded Soundscapes™. Recording artists and media creators can license high-quality 808-heavy melodic trap, trap-soul, and hip-hop instrumentals with instant digital file delivery. Tiers: MP3 Lease $29 · WAV Lease $59 · Unlimited Lease $149 · Exclusive Rights $499 (full trackout stems & complete catalog retirement).",
    link_label: "Explore Beats & Licensing Tiers", link_url: "/beats",
  },
  {
    id: "unsigned-only-2026-semi-finalist",
    date: "July 2026",
    category: "Awards & Industry Recognition",
    title: "Unsigned Only Music Competition Names Heather Da Boa 2026 R&B Semi-Finalist",
    body: "CLEARWATER, SC — Flagship recording artist and producer Heather Da Boa was officially selected as a 2026 Semi-Finalist in the international Unsigned Only Music Competition in the R&B Category for her single \"Alchemy (Turn It To Gold)\". Selected from thousands of global entries, this recognition highlights the growing reach of DGE: Cold-Blooded Records LLC.",
    link_label: null, link_url: null,
  },
  {
    id: "pink-skies-over-aiken",
    date: "July 2026",
    category: "Single Release & Community Outreach",
    title: "DGE Releases Tribute Single & Campaign \"Pink Skies Over Aiken\"",
    body: "DGE: Cold-Blooded Records LLC released the tribute single \"Pink Skies Over Aiken\" in memory of Javeayah Harris. In tandem with the track release, the label and Boa's 33rd Dimension™ launched a community awareness campaign featuring dedicated merchandise, uniting the CSRA community through music and advocacy. Stream the track on Spotify, Apple Music, and YouTube.",
    link_label: "View Apparel at Boa's 33rd Dimension™", link_url: MERCH_STORE_URL,
  },
];

export default function PressPage() {
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);

  useEffect(() => {
    document.title = "News & Press Room | DGE: Cold-Blooded Records LLC™";
    fetch(`${API}/press`)
      .then((r) => r.json())
      .then((d) => {
        if (d.articles && d.articles.length > 0) {
          setArticles(d.articles.map((a) => ({ ...a, id: a.article_id })));
        }
      })
      .catch(() => {});
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="press-page">
      <Navbar />
      <div className="pt-[70px]">
        <section className="relative py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-8">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4 flex items-center gap-2">
                <Megaphone size={14} /> Official News &amp; Press Room
              </p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight" data-testid="press-headline">
                Latest from
                <br />
                <span className="text-stroke-ice">the label.</span>
              </h1>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ice/55">
                <span className="flex items-center gap-2">
                  <Mail size={13} className="text-crystal" />
                  Media Contact:{" "}
                  <a href={`mailto:${PRESS_EMAIL}`} className="text-venom hover:text-white transition-colors" data-testid="press-email-link">
                    {PRESS_EMAIL}
                  </a>
                </span>
                <span className="flex items-center gap-2">
                  <MapPin size={13} className="text-crystal" /> Clearwater, SC / Augusta, GA (CSRA Region)
                </span>
              </div>
            </Reveal>

            <div className="mt-12 space-y-6" data-testid="press-articles">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={Math.min(i, 4) * 0.07}>
                  <article
                    id={`news-${a.id}`}
                    className="group bg-charcoal border border-crystal/20 rounded-2xl overflow-hidden hover:border-venom/50 hover:-translate-y-1 hover:shadow-[0_12px_44px_rgba(57,255,20,0.1)] transition-all duration-400"
                    data-testid={`press-article-${a.id}`}
                  >
                    {a.photo && (
                      <div className="relative h-48 sm:h-56 overflow-hidden">
                        <img
                          src={mediaUrl(a.photo)}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
                      </div>
                    )}
                    <div className="p-6 sm:p-8">
                      <div className="flex flex-wrap items-center gap-2.5 mb-4">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-venom border border-venom/40 rounded-full px-3 py-1">
                          {a.date}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#B983FF] border border-crystal/50 rounded-full px-3 py-1">
                          {a.category}
                        </span>
                      </div>
                      <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-snug">{a.title}</h2>
                      <p className="mt-3.5 text-sm text-ice/70 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                      {a.link_label && a.link_url && (
                        a.link_url.startsWith("/") ? (
                          <Link
                            to={a.link_url}
                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-venom hover:text-white transition-colors duration-300"
                            data-testid={`press-link-${a.id}`}
                          >
                            {a.link_label} <ArrowUpRight size={15} />
                          </Link>
                        ) : (
                          <a
                            href={a.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-venom hover:text-white transition-colors duration-300"
                            data-testid={`press-link-${a.id}`}
                          >
                            {a.link_label} <ExternalLink size={14} />
                          </a>
                        )
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15}>
              <div className="mt-14 bg-charcoal border border-crystal/25 rounded-3xl p-7 sm:p-10 relative overflow-hidden" data-testid="media-kit">
                <div className="absolute -top-20 right-0 w-[320px] h-[200px] bg-crystal/20 blur-[90px] rounded-full" aria-hidden="true" />
                <div className="relative">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium flex items-center gap-2">
                    <FileText size={14} /> Media Kit &amp; Electronic Press Kit (EPK)
                  </p>
                  <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold text-white">
                    Press &amp; media inquiries
                  </h2>
                  <p className="mt-3 text-sm text-ice/60 leading-relaxed max-w-xl">
                    For press inquiries, interview requests, radio bookings, or sync licensing proposals:
                  </p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <p className="text-ice/70"><span className="text-ice/40">Official Portal:</span> cold-blooded-records.com</p>
                    <p className="text-ice/70"><span className="text-ice/40">Official Storefront:</span> the33rd.store</p>
                    <p className="text-ice/70">
                      <span className="text-ice/40">Press Email:</span>{" "}
                      <a href={`mailto:${PRESS_EMAIL}`} className="text-venom hover:text-white transition-colors">{PRESS_EMAIL}</a>
                    </p>
                    <p className="text-ice/70"><span className="text-ice/40">Mailing Address:</span> PO Box 1074, Clearwater, SC 29822</p>
                    <p className="text-ice/70 sm:col-span-2">
                      <span className="text-ice/40">Mandatory Producer Credit:</span>{" "}
                      <span className="text-venom">&quot;Produced by DGE / Cold-Blooded Records&quot;</span> or{" "}
                      <span className="text-venom">&quot;Prod. by DGE&quot;</span>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
